import { getEntryPoc } from "../../../../pages/callingWorklist/services/callingHistoryFields";
import { CallingHistoryLog } from "../../../../pages/callingWorklist/services/callingHistoryService";
import getPoc from "../../../../pages/callingWorklist/table/utils/getPoc";
import { CallSummaryMode } from "./callSummaryMode";
import {
  EMPTY_FIELD_VALUE,
  getSecondStpStatusLabel,
} from "./getSecondStpStatusLabel";

/** A single label / value pair rendered inside a history entry card. */
export interface HistoryField {
  label: string;
  value: string;
}

/** Number of fields shown per row in the entry card. */
const FIELDS_PER_ROW = 2;

/**
 * Returns a trimmed value or the placeholder when it is empty.
 *
 * @input value - Raw stored value
 * @return Display-ready value
 */
const withFallback = (value: string | null | undefined): string =>
  value?.trim() || EMPTY_FIELD_VALUE;

/**
 * Builds the field rows for one history entry. Fax entries show the fax number
 * instead of the phone number and omit the insurance rep, mirroring the form.
 *
 * @input entry - Calling history log entry
 * @input fallbackSecondStpStatus - Row's current status, for entries saved
 *   before the second STP status was snapshotted onto the log
 * @return Field pairs grouped into rows of two
 */
export const buildHistoryFieldRows = (
  entry: CallingHistoryLog,
  fallbackSecondStpStatus: string | null,
): HistoryField[][] => {
  const isFax = entry.mode === CallSummaryMode.FAX;

  const fields: HistoryField[] = [
    isFax
      ? { label: "Fax", value: withFallback(entry.fax_number) }
      : { label: "Phone", value: withFallback(entry.phone_number) },
    { label: "Case ID", value: withFallback(entry.case_id) },
    isFax
      ? { label: "Sent To", value: withFallback(entry.sent_to) }
      : { label: "Insurance Rep", value: withFallback(entry.rep_name) },
    { label: "POC", value: withFallback(getPoc(getEntryPoc(entry))) },
    {
      label: "Second STP Status",
      value: getSecondStpStatusLabel(
        entry.second_stp_status ?? fallbackSecondStpStatus,
      ),
    },
    { label: "Response Status", value: withFallback(entry.response_status) },
    { label: "Case Status", value: withFallback(entry.case_status) },
    { label: "Next Action", value: withFallback(entry.next_action) },
  ];

  const rows: HistoryField[][] = [];
  for (let index = 0; index < fields.length; index += FIELDS_PER_ROW) {
    rows.push(fields.slice(index, index + FIELDS_PER_ROW));
  }

  return rows;
};
